import React, { useState, useEffect } from 'react';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar, 
  ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ScatterChart, Scatter, AreaChart, Area,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Filter, Download, ChevronRight, ChevronLeft,
  BarChart2, LineChart as LineChartIcon, AreaChart as AreaChartIcon,
  PieChart as PieChartIcon, TrendingUp, TrendingDown, Calendar as CalendarIcon,
  ArrowUpRight, ArrowDownRight, CheckSquare, Square, Info,
  Users, ShoppingBag, CreditCard, Activity, RefreshCw,
  Clock, DollarSign, Tag, UserPlus, Percent, LayoutGrid, X
} from 'lucide-react';

import { salesTrends, regions, comparisonData } from '../data/mockData';

// Helper component for comparison metrics cards
const MetricComparisonCard = ({ title, currentValue, previousValue, format, icon: Icon, color = 'blue' }) => {
  const percentChange = ((currentValue - previousValue) / previousValue) * 100;
  const isPositive = percentChange >= 0;
  
  const formatValue = (value) => {
    if (format === 'currency') {
      return value.toLocaleString('en-SA', {
        style: 'currency',
        currency: 'SAR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    if (format === 'percent') {
      return `${value}%`;
    }
    if (format === 'decimal') {
      return value.toFixed(1);
    }
    return value.toLocaleString();
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && (
            <div className={`p-2 rounded-lg bg-${color}-50 dark:bg-${color}-900/20`}>
              <Icon className={`w-5 h-5 text-${color}-500 dark:text-${color}-400`} />
            </div>
          )}
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</h3>
        </div>
        <div className={`flex items-center text-sm ${
          isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }`}>
          {isPositive ? (
            <ArrowUpRight className="w-4 h-4 mr-1" />
          ) : (
            <ArrowDownRight className="w-4 h-4 mr-1" />
          )}
          <span>{Math.abs(percentChange).toFixed(1)}%</span>
        </div>
      </div>
      
      <div className="mt-2 space-y-2">
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatValue(currentValue)}
          </p>
        </div>
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
          <span>vs {formatValue(previousValue)}</span>
        </div>
      </div>
    </motion.div>
  );
};

// DateRangePicker component
const DateRangePicker = ({ selectedPeriods, onSelect }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [customRange, setCustomRange] = useState({
    currentDate: new Date(),
    previousDate: new Date(Date.now() - 24 * 60 * 60 * 1000) // Default to yesterday
  });
  
  const predefinedRanges = [
    { id: 'today_yesterday', label: 'Today vs Yesterday', current: 'today', previous: 'yesterday' },
    { id: 'today_lastWeek', label: 'Today vs Last Week', current: 'today', previous: 'lastWeekSameDay' },
    { id: 'today_lastMonth', label: 'Today vs Last Month', current: 'today', previous: 'lastMonthSameDay' },
    { id: 'today_lastYear', label: 'Today vs Last Year', current: 'today', previous: 'lastYearSameDay' },
    { id: 'custom', label: 'Custom Range', current: 'custom', previous: 'custom' }
  ];
  
  const handleCustomDateChange = (type, date) => {
    setCustomRange(prev => ({
      ...prev,
      [type]: date
    }));
  };
  
  const applyCustomRange = () => {
    // Format dates for display
    const currentDateString = customRange.currentDate.toLocaleDateString();
    const previousDateString = customRange.previousDate.toLocaleDateString();
    
    // Create custom period data objects with the necessary properties
    onSelect({
      current: 'custom',
      previous: 'custom',
      customData: {
        current: {
          date: currentDateString,
          // We'll use today's data as a placeholder
          ...comparisonData.periods.today
        },
        previous: {
          date: previousDateString,
          // We'll use yesterday's data as a placeholder
          ...comparisonData.periods.yesterday
        }
      }
    });
    
    setShowCalendar(false);
  };
  
  return (
    <div className="relative">
      <button
        onClick={() => setShowCalendar(!showCalendar)}
        className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg py-2 px-3 text-sm font-medium shadow-sm"
      >
        <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <span>
          {selectedPeriods.current === 'today' ? 
            predefinedRanges.find(r => r.current === selectedPeriods.current && r.previous === selectedPeriods.previous)?.label : 
            selectedPeriods.current === 'custom' ? 'Custom Range' : 'Select Range'}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
      </button>
      
      {showCalendar && (
        <div className="absolute top-full right-0 mt-2 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-80">
          <div className="space-y-2 mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Predefined Ranges</h3>
            {predefinedRanges.filter(r => r.id !== 'custom').map(range => (
              <button
                key={range.id}
                onClick={() => {
                  onSelect({ current: range.current, previous: range.previous });
                  setShowCalendar(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                  selectedPeriods.current === range.current && selectedPeriods.previous === range.previous
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
          
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Custom Range</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Current Period</label>
                <input 
                  type="date"
                  value={customRange.currentDate.toISOString().split('T')[0]}
                  onChange={(e) => handleCustomDateChange('currentDate', new Date(e.target.value))}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Previous Period</label>
                <input 
                  type="date"
                  value={customRange.previousDate.toISOString().split('T')[0]}
                  onChange={(e) => handleCustomDateChange('previousDate', new Date(e.target.value))}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <button
                onClick={applyCustomRange}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-md py-1.5 text-sm font-medium"
              >
                Apply Custom Range
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Metrics Selector component
const MetricsSelector = ({ availableMetrics, selectedMetrics, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleMetric = (metricId) => {
    if (selectedMetrics.includes(metricId)) {
      onChange(selectedMetrics.filter(id => id !== metricId));
    } else {
      onChange([...selectedMetrics, metricId]);
    }
  };
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg py-2 px-3 text-sm font-medium shadow-sm"
      >
        <LayoutGrid className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <span>Metrics ({selectedMetrics.length})</span>
        <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
      </button>
      
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-72">
          <div className="mb-2 flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Metrics</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {availableMetrics.map(metric => (
              <div 
                key={metric.id}
                className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-md"
              >
                <button
                  onClick={() => toggleMetric(metric.id)}
                  className="flex items-center flex-1"
                >
                  {selectedMetrics.includes(metric.id) ? (
                    <CheckSquare className="w-5 h-5 text-blue-500 dark:text-blue-400 mr-2" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" />
                  )}
                  <span className={`text-sm ${
                    selectedMetrics.includes(metric.id)
                      ? 'font-medium text-gray-900 dark:text-white'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {metric.name}
                  </span>
                </button>
                <div
                  className="w-3 h-3 rounded-full ml-2"
                  style={{ backgroundColor: metric.color }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ChevronDown = ({ className }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <path d="m6 9 6 6 6-6"/>
    </svg>
  );
};

const ComparisonTab = ({ timeRange }) => {
  const [selectedPeriods, setSelectedPeriods] = useState({ current: 'today', previous: 'yesterday' });
  const [selectedMetrics, setSelectedMetrics] = useState(['sales', 'transactions', 'customers', 'avgTicket']);
  const [chartType, setChartType] = useState('bars');
  const [viewMode, setViewMode] = useState('detail');
  const [selectedDetailMetric, setSelectedDetailMetric] = useState('sales');
  const [periodData, setPeriodData] = useState({
    current: comparisonData.periods.today,     // Initialize with default values
    previous: comparisonData.periods.yesterday // Initialize with default values
  });
  
  // Get all available metrics from the data
  const availableMetrics = comparisonData.metrics;
  
  // Set period data based on selectedPeriods
  useEffect(() => {
    let currentData, previousData;
    
    if (selectedPeriods.current === 'custom' && selectedPeriods.customData) {
      // Use custom data if available
      currentData = selectedPeriods.customData.current;
      previousData = selectedPeriods.customData.previous;
    } else {
      // Otherwise use predefined periods
      currentData = comparisonData.periods[selectedPeriods.current] || comparisonData.periods.today;
      previousData = comparisonData.periods[selectedPeriods.previous] || comparisonData.periods.yesterday;
    }
    
    setPeriodData({
      current: currentData,
      previous: previousData
    });
  }, [selectedPeriods]);
  
  // Prepare data for hourly comparison chart
  const hourlyData = comparisonData.hourlyData.map(hour => {
    const hourData = {
      timeLabel: hour.timeLabel,
    };
    
    // Add data for selected metrics
    selectedMetrics.forEach(metric => {
      hourData[`current_${metric}`] = hour.current[metric];
      hourData[`previous_${metric}`] = hour.previous[metric];
    });
    
    return hourData;
  });
  
  // Icons mapped to metrics
  const metricIcons = {
    sales: DollarSign,
    transactions: ShoppingBag,
    customers: Users,
    avgTicket: CreditCard,
    conversion: Percent,
    unitsPerTransaction: Tag,
    trafficCount: Activity,
    staffCount: Users,
    baskets: ShoppingBag,
    returnRate: RefreshCw,
    newCustomers: UserPlus,
    salesPerSqMeter: DollarSign,
    salesPerStaffHour: Clock
  };
  
  // Format value based on metric type
  const formatValue = (value, format) => {
    if (format === 'currency') {
      return value.toLocaleString('en-SA', {
        style: 'currency',
        currency: 'SAR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    if (format === 'percent') {
      return `${value}%`;
    }
    if (format === 'decimal') {
      return value.toFixed(1);
    }
    return value.toLocaleString();
  };
  
  // Custom tooltip for the hourly chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const currentData = {};
      const previousData = {};
      
      // Organize data in current/previous groups
      payload.forEach(p => {
        const [period, metric] = p.dataKey.split('_');
        if (period === 'current') {
          currentData[metric] = p.value;
        } else {
          previousData[metric] = p.value;
        }
      });
      
      return (
        <div className="bg-white dark:bg-gray-800 p-3 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">{label}</p>
          <div className="space-y-1.5">
            {Object.keys(currentData).map(metric => {
              const metricInfo = availableMetrics.find(m => m.id === metric);
              const currentVal = currentData[metric];
              const previousVal = previousData[metric];
              const percentChange = previousVal ? ((currentVal - previousVal) / previousVal) * 100 : 0;
              
              return (
                <div key={metric} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{metricInfo?.name || metric}:</span>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatValue(currentVal, metricInfo?.format || 'number')}
                    </span>
                    <span className={`ml-2 text-xs ${
                      percentChange >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {percentChange >= 0 ? '+' : ''}{percentChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Comparison Analysis</h2>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('detail')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  viewMode === 'detail' 
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                }`}
              >
                Detailed View
              </button>
              <button
                onClick={() => setViewMode('hourly')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  viewMode === 'hourly' 
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                }`}
              >
                Hourly Analysis
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <DateRangePicker 
              selectedPeriods={selectedPeriods}
              onSelect={setSelectedPeriods}
            />
            
            <MetricsSelector
              availableMetrics={availableMetrics}
              selectedMetrics={selectedMetrics}
              onChange={setSelectedMetrics}
            />
            
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg p-1">
              <button
                onClick={() => setChartType('bars')}
                className={`p-1.5 rounded ${
                  chartType === 'bars' 
                    ? 'bg-white dark:bg-gray-800 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <BarChart2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setChartType('lines')}
                className={`p-1.5 rounded ${
                  chartType === 'lines' 
                    ? 'bg-white dark:bg-gray-800 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <LineChartIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setChartType('area')}
                className={`p-1.5 rounded ${
                  chartType === 'area' 
                    ? 'bg-white dark:bg-gray-800 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <AreaChartIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Period Information */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 text-sm">
        <div className="flex items-center justify-between text-gray-600 dark:text-gray-400">
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300 mr-2">Current Period:</span>
            {periodData.current?.date || 'Today'}
          </div>
          <div className="flex items-center">
            <span className="font-medium text-gray-700 dark:text-gray-300 mr-2">Previous Period:</span>
            {periodData.previous?.date || 'Yesterday'}
          </div>
        </div>
      </div>

      {/* Hourly Comparison Charts - Hourly Mode */}
      {viewMode === 'hourly' && (
        <div className="space-y-6">
          {/* Time Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Hourly Comparison</h3>
            
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'bars' ? (
                  <BarChart
                    data={hourlyData}
                    margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timeLabel" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {selectedMetrics.slice(0, 1).map(metric => {
                      const metricInfo = availableMetrics.find(m => m.id === metric);
                      return (
                        <React.Fragment key={metric}>
                          <Bar 
                            dataKey={`current_${metric}`} 
                            name={`Current: ${metricInfo?.name || metric}`} 
                            fill={metricInfo?.color || '#3B82F6'} 
                            fillOpacity={0.8}
                            stackId={metric}
                          />
                          <Bar 
                            dataKey={`previous_${metric}`} 
                            name={`Previous: ${metricInfo?.name || metric}`} 
                            fill={metricInfo?.color || '#3B82F6'} 
                            fillOpacity={0.4}
                            stackId={metric}
                          />
                        </React.Fragment>
                      );
                    })}
                  </BarChart>
                ) : chartType === 'lines' ? (
                  <LineChart
                    data={hourlyData}
                    margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timeLabel" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {selectedMetrics.slice(0, 2).map(metric => {
                      const metricInfo = availableMetrics.find(m => m.id === metric);
                      return (
                        <React.Fragment key={metric}>
                          <Line 
                            type="monotone" 
                            dataKey={`current_${metric}`} 
                            name={`Current: ${metricInfo?.name || metric}`} 
                            stroke={metricInfo?.color || '#3B82F6'} 
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey={`previous_${metric}`} 
                            name={`Previous: ${metricInfo?.name || metric}`} 
                            stroke={metricInfo?.color || '#3B82F6'} 
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                        </React.Fragment>
                      );
                    })}
                  </LineChart>
                ) : (
                  <AreaChart
                    data={hourlyData}
                    margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timeLabel" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {selectedMetrics.slice(0, 2).map(metric => {
                      const metricInfo = availableMetrics.find(m => m.id === metric);
                      return (
                        <React.Fragment key={metric}>
                          <Area 
                            type="monotone" 
                            dataKey={`current_${metric}`} 
                            name={`Current: ${metricInfo?.name || metric}`} 
                            stroke={metricInfo?.color || '#3B82F6'} 
                            fill={metricInfo?.color || '#3B82F6'} 
                            fillOpacity={0.4}
                            activeDot={{ r: 5 }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey={`previous_${metric}`} 
                            name={`Previous: ${metricInfo?.name || metric}`} 
                            stroke={metricInfo?.color || '#3B82F6'} 
                            fill={metricInfo?.color || '#3B82F6'} 
                            fillOpacity={0.2}
                            strokeDasharray="5 5"
                            activeDot={{ r: 5 }}
                          />
                        </React.Fragment>
                      );
                    })}
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Metric Selector tabs for hourly view */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Select Metric to Visualize</h3>
            <div className="flex flex-wrap gap-2">
              {selectedMetrics.map(metric => {
                const metricInfo = availableMetrics.find(m => m.id === metric);
                return (
                  <button
                    key={metric}
                    onClick={() => setSelectedDetailMetric(metric)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      selectedDetailMetric === metric
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    }`}
                    style={{ 
                      backgroundColor: selectedDetailMetric === metric ? `${metricInfo?.color}15` : undefined,
                      color: selectedDetailMetric === metric ? metricInfo?.color : undefined
                    }}
                  >
                    {metricInfo?.name || metric}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Detailed View Mode */}
      {viewMode === 'detail' && (
        <div className="space-y-6">
          {/* Metric Line Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Metric Comparison
              </h3>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Select metrics to visualize
              </div>
            </div>
            
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={[
                    { name: 'Current', ...selectedMetrics.reduce((acc, metric) => ({...acc, [metric]: periodData?.current?.[metric] || 0}), {}) },
                    { name: 'Previous', ...selectedMetrics.reduce((acc, metric) => ({...acc, [metric]: periodData?.previous?.[metric] || 0}), {}) }
                  ]}
                  margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      const metricInfo = availableMetrics.find(m => m.id === name);
                      return [formatValue(value, metricInfo?.format || 'number'), metricInfo?.name || name];
                    }}
                  />
                  <Legend />
                  {selectedMetrics.map(metric => {
                    const metricInfo = availableMetrics.find(m => m.id === metric);
                    return (
                      <Line
                        key={metric}
                        type="monotone"
                        dataKey={metric}
                        name={metricInfo?.name || metric}
                        stroke={metricInfo?.color || '#3B82F6'}
                        strokeWidth={2}
                        dot={{ r: 5 }}
                        activeDot={{ r: 8 }}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            {/* Metric selection buttons */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap gap-2">
                {availableMetrics.map(metric => (
                  <button
                    key={metric.id}
                    onClick={() => {
                      if (selectedMetrics.includes(metric.id)) {
                        setSelectedMetrics(prev => prev.filter(id => id !== metric.id));
                      } else {
                        setSelectedMetrics(prev => [...prev, metric.id]);
                      }
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      selectedMetrics.includes(metric.id)
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    }`}
                    style={{ 
                      backgroundColor: selectedMetrics.includes(metric.id) ? `${metric.color}15` : undefined,
                      color: selectedMetrics.includes(metric.id) ? metric.color : undefined
                    }}
                  >
                    {metric.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Multi-metric comparison charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Side-by-side Bar chart */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Side-by-Side Comparison
              </h3>
              
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={selectedMetrics.map(metricId => {
                      const metricInfo = availableMetrics.find(m => m.id === metricId);
                      return {
                        name: metricInfo?.name || metricId,
                        current: periodData?.current?.[metricId] || 0,
                        previous: periodData?.previous?.[metricId] || 0
                      };
                    })}
                    layout="vertical"
                    margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" />
                    <Tooltip
                      formatter={(value, name) => {
                        const metric = selectedMetrics.find(
                          metricId => availableMetrics.find(m => m.id === metricId)?.name === name ||
                                     metricId === name
                        );
                        const metricInfo = availableMetrics.find(m => m.id === metric);
                        return [formatValue(value, metricInfo?.format || 'number'), name];
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="current" 
                      name="Current Period" 
                      fill="#3B82F6"
                      radius={[0, 4, 4, 0]} 
                    />
                    <Bar 
                      dataKey="previous" 
                      name="Previous Period" 
                      fill="#F97316"
                      radius={[0, 4, 4, 0]} 
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Growth percentage chart */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Growth Analysis
              </h3>
              
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={selectedMetrics.map(metricId => {
                      const metricInfo = availableMetrics.find(m => m.id === metricId);
                      const currentValue = periodData?.current?.[metricId] || 0;
                      const previousValue = periodData?.previous?.[metricId] || 0;
                      // Avoid division by zero
                      const percentChange = previousValue !== 0 
                        ? ((currentValue - previousValue) / previousValue) * 100 
                        : currentValue > 0 ? 100 : 0;
                      
                      return {
                        name: metricInfo?.name || metricId,
                        growth: percentChange,
                        positive: percentChange >= 0
                      };
                    })}
                    layout="vertical"
                    margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={(value) => `${value}%`} />
                    <YAxis type="category" dataKey="name" />
                    <Tooltip formatter={(value) => [`${value.toFixed(2)}%`, 'Growth']} />
                    <Legend />
                    <Bar
                      dataKey="growth"
                      name="Growth %"
                      radius={[0, 4, 4, 0]}
                    >
                      {selectedMetrics.map((metricId, index) => {
                        const currentValue = periodData?.current?.[metricId] || 0;
                        const previousValue = periodData?.previous?.[metricId] || 0;
                        // Avoid division by zero
                        const percentChange = previousValue !== 0 
                          ? ((currentValue - previousValue) / previousValue) * 100 
                          : currentValue > 0 ? 100 : 0;
                        
                        return (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={percentChange >= 0 ? '#10B981' : '#EF4444'}
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Detailed metrics grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {selectedMetrics.map(metricId => {
              const metricInfo = availableMetrics.find(m => m.id === metricId);
              const IconComponent = metricIcons[metricId] || Activity;
              
              // Calculate percentage change with null checks
              const currentValue = periodData?.current?.[metricId] || 0;
              const previousValue = periodData?.previous?.[metricId] || 0;
              // Avoid division by zero
              const percentChange = previousValue !== 0 
                ? ((currentValue - previousValue) / previousValue) * 100
                : currentValue > 0 ? 100 : 0;
              const isPositive = percentChange >= 0;
              
              return (
                <div
                  key={metricId}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg" style={{ backgroundColor: `${metricInfo?.color}15` }}>
                        <IconComponent style={{ color: metricInfo?.color }} className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {metricInfo?.name || metricId}
                      </h3>
                    </div>
                    <div className={`flex items-center justify-center px-2.5 py-1 rounded-full text-sm font-medium ${
                      isPositive ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                    }`}>
                      {isPositive ? (
                        <ArrowUpRight className="w-4 h-4 mr-1" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 mr-1" />
                      )}
                      <span>{Math.abs(percentChange).toFixed(1)}%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-stretch gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Current Period</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatValue(currentValue, metricInfo?.format || 'number')}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{periodData?.current?.date || 'Today'}</div>
                    </div>
                    
                    <div className="flex-1 space-y-2">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Previous Period</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatValue(previousValue, metricInfo?.format || 'number')}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{periodData?.previous?.date || 'Yesterday'}</div>
                    </div>
                    
                    <div className="w-[100px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Current', value: currentValue },
                              { name: 'Previous', value: previousValue }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius="60%"
                            outerRadius="90%"
                            startAngle={90}
                            endAngle={-270}
                            dataKey="value"
                          >
                            <Cell fill={metricInfo?.color || '#3B82F6'} />
                            <Cell fill={`${metricInfo?.color}50` || '#3B82F680'} />
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonTab; 